import { useMemo } from 'react';
import styled from 'styled-components';

import { BindingCreator } from '@dreryk/react-common';
import {
   CheckboxGroup,
   CheckboxGroupItemRenderer,
   CheckboxGroupValue,
   Scroll,
   StyledFlex,
   Textarea,
} from '@dreryk/react-components';

import { medicalDevicesTranslationKeys } from '../../../translations';
import {
   AdditionalIndicationsFieldValue,
   MedicalDeviceOrderAdditionalIndicationsFormBeanPart,
   MedicalDeviceOrderFormWatch,
} from '../../medicalDeviceOrder.types';
import { ShorteningDeviceUseTimeSpanReason } from '../ShorteningDeviceUseTimeSpanReason';

import { ShorteningDeviceUseTimeSpanJustificationTooltip } from './ShorteningDeviceUseTimeSpanJustificationTooltip';

type AdditionalIndicationsSectionProps = {
   readonly register: BindingCreator<MedicalDeviceOrderAdditionalIndicationsFormBeanPart>;
   readonly watch: MedicalDeviceOrderFormWatch<MedicalDeviceOrderAdditionalIndicationsFormBeanPart>;
   readonly showReasonField: boolean;
};

const StyledShorteningDeviceUseTimeSpanJustification = styled.div`
   height: 115px;
`;
StyledShorteningDeviceUseTimeSpanJustification.displayName = 'StyledShorteningDeviceUseTimeSpanJustification';

const shorteningDeviceUseTimeSpanJustificationRenderer: CheckboxGroupItemRenderer = (CheckboxComponent) => (
   <StyledFlex gap={'4px'} alignItems={'center'}>
      <CheckboxComponent />
      <ShorteningDeviceUseTimeSpanJustificationTooltip />
   </StyledFlex>
);

const hasShorteningDeviceUseTimeSpan = (additionalIndications: AdditionalIndicationsFieldValue): boolean =>
   Boolean(additionalIndications?.some((value) => value === 'shorteningDeviceUseTimeSpan'));

const SHORTENING_DEVICE_USE_TIME_SPAN_REASON_MAX_LENGTH = 500;

export const AdditionalIndicationsSection = (props: AdditionalIndicationsSectionProps): JSX.Element => {
   const availableValues: CheckboxGroupValue[] = useMemo(
      () => [
         {
            key: 'shorteningDeviceUseTimeSpan',
            label: medicalDevicesTranslationKeys.fields.shorteningDeviceUseTimeSpan,
            defaultValue: false,
            renderer: shorteningDeviceUseTimeSpanJustificationRenderer,
         },
      ],
      []
   );
   const additionalIndications = props.watch('additionalIndications');
   return (
      <>
         <CheckboxGroup
            testId={'additionalIndications'}
            controller={props.register('additionalIndications')}
            label={medicalDevicesTranslationKeys.fields.additionalIndications}
            availableValues={availableValues}
         />
         {hasShorteningDeviceUseTimeSpan(additionalIndications) ? (
            <>
               {props.showReasonField ? <ShorteningDeviceUseTimeSpanReason register={props.register} /> : null}
               <Scroll testId={'shorteningDeviceUseTimeSpanJustification'}>
                  <StyledShorteningDeviceUseTimeSpanJustification>
                     <Textarea
                        testId={'shorteningDeviceUseTimeSpanJustification'}
                        controller={props.register('shorteningDeviceUseTimeSpanJustification', {
                           required: true,
                        })}
                        label={medicalDevicesTranslationKeys.fields.justification}
                        maxLength={SHORTENING_DEVICE_USE_TIME_SPAN_REASON_MAX_LENGTH}
                        placeholder={medicalDevicesTranslationKeys.fields.fillDeviceTimeSpanShorteningJustification}
                        fillParent
                     />
                  </StyledShorteningDeviceUseTimeSpanJustification>
               </Scroll>
            </>
         ) : null}
      </>
   );
};
